import { Role } from '@plato/data/prisma';
import { useCallback } from 'react';
import { z } from 'zod';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocalStorage } from '../../../lib/hooks/use-local-storage.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

const VIEW_MODE_STORAGE_KEY = 'plato-view-mode';

const viewModeSchema = z.enum(['MANAGER', 'REP']);

export type ViewMode = z.infer<typeof viewModeSchema>;

/**
 * Whether the manager view experience exists for the current user at all:
 * the user is a manager (MANAGER / SUPER_MANAGER). The manager view is the
 * default experience for managers — there is no longer a flag gating it.
 *
 * When false (non-managers), the user gets the flat rep menu.
 */
export const useManagerViewEnabled = (): boolean => {
    const { platoUser } = useAppState();

    return platoUser.role === Role.MANAGER || platoUser.role === Role.SUPER_MANAGER;
};

/**
 * The persisted Manager/Rep view choice. Managers default to MANAGER; users
 * without the manager experience are hard-locked to REP regardless of any
 * stored value (the stored value is validated but never consulted for them).
 *
 * `setViewMode` centrally fires the `view_mode_switched` analytics event so
 * every switch source (sidebar dropdown, manager-view-link confirm modal)
 * is captured exactly once per change.
 */
export const useViewMode = (): { viewMode: ViewMode; setViewMode: (next: ViewMode) => void } => {
    const managerViewEnabled = useManagerViewEnabled();
    const sendEvent = useAnalytics();

    const [storedViewMode, setStoredViewMode] = useLocalStorage<ViewMode>(VIEW_MODE_STORAGE_KEY, 'MANAGER', {
        schema: viewModeSchema,
    });

    const viewMode: ViewMode = managerViewEnabled ? storedViewMode : 'REP';

    const setViewMode = useCallback(
        (next: ViewMode) => {
            setStoredViewMode((prev) => {
                if (prev !== next) {
                    sendEvent('view_mode_switched', { view_mode: next });
                }
                return next;
            });
        },
        [setStoredViewMode, sendEvent],
    );

    return { viewMode, setViewMode };
};

/**
 * THE manager-view gate. True only for a manager who is currently in Manager
 * view. All manager-only chrome (grouped
 * menu, manager-only nav items, manager-only columns) is added only when this
 * is true — reps never receive it by construction.
 */
export const useIsManagerView = (): boolean => {
    const managerViewEnabled = useManagerViewEnabled();
    const { viewMode } = useViewMode();

    return managerViewEnabled && viewMode === 'MANAGER';
};
